// Phase 3.13 (hh-php) — SeasonalScreen.
//
// Ported from designproject/files/screens-notifs.jsx (`SeasonalScreen`,
// `SeasonRow`). The design held items in local state with a free `toggle`;
// here the checklist is fetched from GET /api/v1/seasonal/{season} and
// completing an item POSTs /api/v1/seasonal/{id}/done. A season selector is
// added (the design hard-coded summer) — it defaults to the current season.

import type { JSX } from 'preact';
import { useEffect, useMemo, useState } from 'preact/hooks';
import { T, Icon, Chip, Avatar, type IconName } from '../../tokens';
import { ScreenHeader } from '../../shell';
import type { RouterApi } from '../../router';
import { seasonal, humanizeError } from '../../api';
import type { Season, SeasonalItem } from '../../api/endpoints/seasonal.js';
import { SEASONS } from '../../api/endpoints/seasonal.js';

export interface SeasonalScreenProps {
  readonly router: RouterApi;
  readonly season?: Season;
}

interface SeasonMeta {
  readonly label: string;
  readonly title: string;
  readonly icon: IconName;
}

const SEASON_META: Record<Season, SeasonMeta> = {
  summer: { label: 'Лето', title: 'Готовим дом к лету', icon: 'sun' },
  winter: { label: 'Зима', title: 'Готовим дом к зиме', icon: 'snow' },
  spring: { label: 'Весна', title: 'Готовим дом к весне', icon: 'leaf' },
  autumn: { label: 'Осень', title: 'Готовим дом к осени', icon: 'leaf' },
};

/** Northern-hemisphere season for the current month. */
function currentSeason(): Season {
  const m = new Date().getMonth(); // 0..11
  if (m >= 2 && m <= 4) return 'spring';
  if (m >= 5 && m <= 7) return 'summer';
  if (m >= 8 && m <= 10) return 'autumn';
  return 'winter';
}

function describeError(err: unknown): string {
  return humanizeError(err, 'load');
}

type Status = 'loading' | 'ready' | 'error';

export function SeasonalScreen({ router, season }: SeasonalScreenProps): JSX.Element {
  const [active, setActive] = useState<Season>(season ?? currentSeason());
  const [status, setStatus] = useState<Status>('loading');
  const [items, setItems] = useState<readonly SeasonalItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<readonly string[]>([]);

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    setError(null);
    seasonal
      .getSeasonalChecklist(active)
      .then((res) => {
        if (cancelled) return;
        setItems(res.items);
        setStatus('ready');
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(describeError(err));
        setStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, [active]);

  const meta = SEASON_META[active];
  const doneCount = useMemo(() => items.filter((i) => i.isDone).length, [items]);

  const toggle = (item: SeasonalItem): void => {
    if (busy.includes(item.id)) return;
    const next = !item.isDone;
    setBusy((b) => [...b, item.id]);
    // Optimistic flip.
    setItems((list) => list.map((i) => (i.id === item.id ? { ...i, isDone: next } : i)));
    seasonal
      .setSeasonalItemDone(item.id, next)
      .catch((err: unknown) => {
        setError(describeError(err));
        // Roll back.
        setItems((list) =>
          list.map((i) => (i.id === item.id ? { ...i, isDone: !next } : i)),
        );
      })
      .finally(() => {
        setBusy((b) => b.filter((x) => x !== item.id));
      });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: T.bg }}>
      <ScreenHeader
        title={meta.title}
        subtitle={`${doneCount} из ${items.length} · по твоему оборудованию`}
        onBack={() => router.back()}
      />
      <div style={{ flex: 1, overflow: 'auto', padding: '4px 16px 24px' }}>
        {/* Season selector */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14 }}>
          {SEASONS.map((s) => {
            const selected = s === active;
            return (
              <button
                key={s}
                type="button"
                onClick={() => setActive(s)}
                style={{
                  padding: '7px 14px',
                  background: selected ? T.brand : T.surface,
                  color: selected ? '#fff' : T.ink,
                  border: `1px solid ${selected ? T.brand : T.border}`,
                  borderRadius: 999,
                  fontFamily: T.font,
                  fontSize: 13,
                  fontWeight: 650,
                  cursor: 'pointer',
                }}
              >
                {SEASON_META[s].label}
              </button>
            );
          })}
        </div>

        <div
          style={{
            background: T.brandSoft,
            border: `1px solid ${T.brandSoft2}`,
            borderRadius: 16,
            padding: 16,
            display: 'flex',
            alignItems: 'center',
            gap: 12,
          }}
        >
          <Avatar kind="ai" icon={meta.icon} size={44} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: T.brandDeep }}>
              {meta.label} уже скоро
            </div>
            <div style={{ fontSize: 12.5, color: T.ink2, marginTop: 3 }}>
              Список собран под твоё оборудование
            </div>
          </div>
        </div>

        {status === 'loading' && (
          <div style={{ fontSize: 12.5, color: T.ink3, padding: '14px 2px' }}>Загружаю…</div>
        )}
        {status === 'error' && error !== null && <ErrorBanner message={error} />}

        {status === 'ready' && items.length === 0 && (
          <EmptyState text="По этому сезону пока ничего не нужно." />
        )}

        {status === 'ready' && items.length > 0 && (
          <>
            <div
              style={{
                marginTop: 14,
                background: T.surface,
                border: `1px solid ${T.border}`,
                borderRadius: 14,
                overflow: 'hidden',
              }}
            >
              {items.map((it, i) => (
                <div key={it.id}>
                  {i > 0 && (
                    <div style={{ height: 1, background: T.divider, marginLeft: 50 }} />
                  )}
                  <SeasonRow
                    item={it}
                    busy={busy.includes(it.id)}
                    onToggle={() => toggle(it)}
                  />
                </div>
              ))}
            </div>

            <div
              style={{
                marginTop: 14,
                padding: '12px 14px',
                background: T.surfaceAlt,
                border: `1px solid ${T.border}`,
                borderRadius: 12,
                fontSize: 12.5,
                color: T.ink3,
                fontWeight: 500,
              }}
            >
              Отмеченное «сделано» можно снять обратно — изменение обратимо.
            </div>

            {error !== null && <ErrorBanner message={error} />}
          </>
        )}
      </div>
    </div>
  );
}

interface SeasonRowProps {
  readonly item: SeasonalItem;
  readonly busy: boolean;
  readonly onToggle: () => void;
}

function SeasonRow({ item, busy, onToggle }: SeasonRowProps): JSX.Element {
  return (
    <div
      onClick={busy ? undefined : onToggle}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: 14,
        cursor: busy ? 'wait' : 'pointer',
        opacity: busy ? 0.6 : 1,
      }}
    >
      <div
        style={{
          width: 22,
          height: 22,
          borderRadius: 11,
          border: `2px solid ${item.isDone ? T.brand : T.borderStrong}`,
          background: item.isDone ? T.brand : 'transparent',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {item.isDone && <Icon name="check" size={14} color="#fff" strokeWidth={2.5} />}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 14,
            fontWeight: 600,
            color: T.ink,
            textDecoration: item.isDone ? 'line-through' : 'none',
            opacity: item.isDone ? 0.5 : 1,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            flexWrap: 'wrap',
          }}
        >
          {item.title}
          {item.isSafety && (
            <Chip tone="safety" icon="shield">
              safety
            </Chip>
          )}
        </div>
      </div>
    </div>
  );
}

function EmptyState({ text }: { readonly text: string }): JSX.Element {
  return (
    <div
      style={{
        marginTop: 14,
        padding: '20px 14px',
        background: T.surfaceAlt,
        border: `1px solid ${T.border}`,
        borderRadius: 14,
        fontSize: 13,
        color: T.ink3,
        textAlign: 'center',
        fontWeight: 500,
      }}
    >
      {text}
    </div>
  );
}

function ErrorBanner({ message }: { readonly message: string }): JSX.Element {
  return (
    <div
      style={{
        marginTop: 12,
        padding: '10px 12px',
        background: T.dangerSoft,
        border: '1px solid #E9C7BF',
        borderRadius: 12,
        fontSize: 12.5,
        color: T.danger,
        fontWeight: 600,
      }}
    >
      {message}
    </div>
  );
}
